import React, { FC, useState } from 'react';
import {
  EditableOutlined,
  LaunchUniversal,
  MoreInfoOutlined,
  TrashCanOutlined,
} from '@duxton-web/icons';
import {
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { Rule } from '@duxton-web/ui/es/form';
import scribe from '@grab-x/scribe-web-sdk';
import { styled } from 'styled-components';

import { useVariables } from '../contexts/ExperimentFormInfoProvider';
import { ExpFormField, VariableType } from '../helper/consts';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import { Variable } from '@/lib/App';

import './TreatmentCard.css';

import DeleteTreatmentModal from '../Modals/DeleteTreatmentModal';
import EditTreatmentModal from '../Modals/EditTreatmentModal';
import ColorIndicator from './ColorIndicator';
import JsonEditor from './JsonEditor';
import JsonEditorModal from '@/components/json-editor-modal';
import { multipleOfToPrecisionConversion } from '@/features/variable/settings/validation/number-range';

interface TreatmentCardProps {
  treatmentId: string;
  treatmentName: string;
  description?: string;
  color?: string;
  editable?: boolean;
  deletable?: boolean;
  onEdit?: (name: string, description: string) => void;
  onDelete?: () => void;
  empty?: boolean;
  disabled?: boolean;
}

const TreatmentCard: FC<TreatmentCardProps> = ({
  treatmentId,
  treatmentName,
  description,
  color = '#E8E8E8',
  editable = true,
  deletable = false,
  onEdit = () => {},
  onDelete = () => {},
  empty,
  disabled,
}) => {
  const [showEditTreatmentModal, setShowEditTreatmentModal] = useState(false);
  const [showEditJsonModal, setShowEditJsonModal] = useState({
    show: false,
    variableName: null,
    value: '',
    jsonSchema: '',
  });
  const [showDeleteTreatmentModal, setShowDeleteTreatmentModal] =
    useState(false);

  const isEmpty = (variableName: string): string => {
    const emptyValues = [null, '', undefined];
    return emptyValues.includes(formVariables?.[variableName]?.[treatmentId])
      ? 'error'
      : '';
  };

  const [variables] = useVariables();

  const form = Form.useFormInstance();

  const formVariables = Form.useWatch(ExpFormField.VARIABLES.ROOT, {
    form,
    preserve: true,
  });

  const renderAddVariableEmptyState = () => (
    <>
      <Typography.Text strong>
        Add Variable to Start Experimenting
      </Typography.Text>
      <Typography.Text type="secondary">
        Start by adding a variable which will reflect on all treatments where
        you can change it’s value for each treatment.
      </Typography.Text>
    </>
  );

  const getRules = (variable: Variable) => {
    const rules: Rule[] = [
      {
        validator(_rule, _value, callback) {
          callback();
        },
      },
    ];

    const jsonSchema = variable?.metadata?.jsonSchema;
    const variableType = variable?.type;

    if (
      !jsonSchema ||
      [VariableType.BOOLEAN, VariableType.JSON, VariableType.MAP].includes(
        variableType,
      )
    )
      return rules;

    const parsedJSONSchema = JSON.parse(jsonSchema) as any;
    const values = parsedJSONSchema?.enum;

    if (values) {
      rules.push({
        type: 'enum',
        enum: values,
      });

      return rules;
    }

    let min: number = null;
    let max: number = null;
    let errorMessage: string;

    if (variableType === VariableType.STRING) {
      min = parsedJSONSchema?.minLength;
      max = parsedJSONSchema?.maxLength;

      if (min != null && max != null)
        errorMessage = `Length of string must be between ${min} and ${max} characters`;
      else if (min != null)
        errorMessage = `Length of string must be no less than ${min} characters`;
      else if (max != null)
        errorMessage = `Length of string must be no longer than ${max} characters;`;
    } else {
      min = parsedJSONSchema?.minimum;
      max = parsedJSONSchema?.maximum;

      if (min != null && max != null)
        errorMessage = `Value must be between ${min} and ${max}`;
      else if (min != null) errorMessage = `Value cannot be lesser than ${min}`;
      else if (max != null)
        errorMessage = `Value cannot be greater than ${max}`;
    }

    rules.push({
      type: variableType === VariableType.STRING ? 'string' : 'number',
      min: min,
      max: max,
      message: errorMessage,
      transform: (value) => {
        switch (variableType) {
          case VariableType.INT64:
            return parseInt(value, 10);
          case VariableType.FLOAT64:
            return parseFloat(value);
          default:
            return value;
        }
      },
    });

    return rules;
  };

  const renderInput = (variable: Variable, treatmentId) => {
    const variableType = variable?.type;
    const variableName = variable?.name;
    const jsonSchema = variable?.metadata?.jsonSchema;
    const parsedJSONSchema = jsonSchema ? JSON.parse(jsonSchema) : null;
    const values = parsedJSONSchema?.enum;

    if (values) {
      return (
        <InputContainer>
          <Select
            options={values.map((value) => {
              return {
                value: value,
                label: value,
              };
            })}
            value={formVariables?.[variableName]?.[treatmentId]}
            onSelect={(value) => {
              form.setFieldValue(
                ExpFormField.VARIABLES.TREATMENT_VALUE(
                  variableName,
                  treatmentId,
                ),
                value,
              );
            }}
          />
        </InputContainer>
      );
    }

    if (variableType === VariableType.STRING)
      return (
        <InputContainer>
          <Input
            status={isEmpty(variableName) ? 'error' : ''}
            value={formVariables?.[variableName]?.[treatmentId]}
          />
        </InputContainer>
      );
    if (variableType === VariableType.INT64)
      return (
        <InputContainer>
          <InputNumber
            className={isEmpty(variableName)}
            style={{ width: '100%' }}
            precision={0}
            type="number"
            value={formVariables?.[variableName]?.[treatmentId]}
          />
        </InputContainer>
      );
    if (variableType === VariableType.FLOAT64) {
      return (
        <InputContainer>
          <InputNumber
            className={isEmpty(variableName)}
            style={{ width: '100%' }}
            precision={multipleOfToPrecisionConversion(
              parsedJSONSchema?.multipleOf,
            )}
            type="number"
            value={formVariables?.[variableName]?.[treatmentId]}
          />
        </InputContainer>
      );
    }
    if (variableType === VariableType.BOOLEAN)
      return (
        <InputContainer>
          <Select
            style={{
              width: '100%',
            }}
            status={
              formVariables?.[variableName]?.[treatmentId] === undefined
                ? 'error'
                : ''
            }
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
            value={
              formVariables?.[variableName]?.[treatmentId] === 1
                ? true
                : formVariables?.[variableName]?.[treatmentId] === 0
                  ? false
                  : undefined
            }
            onSelect={(value) => {
              form.setFieldValue(
                ExpFormField.VARIABLES.TREATMENT_VALUE(
                  variableName,
                  treatmentId,
                ),
                value,
              );
            }}
          />
        </InputContainer>
      );
    if (
      variableType === VariableType.JSON ||
      variableType === VariableType.MAP
    ) {
      const variableJsonString =
        form.getFieldValue(
          ExpFormField.VARIABLES.TREATMENT_VALUE(variableName, treatmentId),
        ) || '';
      return (
        <JsonEditor
          className={isEmpty(variableName)}
          data={String(variableJsonString)}
          actionButtonIcon={<LaunchUniversal />}
          onClickActionButton={() => {
            setShowEditJsonModal({
              show: true,
              variableName,
              value: variableJsonString,
              jsonSchema: jsonSchema,
            });
          }}
          height="150px"
          readOnly
          wrapEnabled
        />
      );
    }
    return (
      <Input
        className={isEmpty(variableName)}
        value={formVariables?.[variableName]?.[treatmentId]}
      />
    );
  };

  return (
    <>
      <Card
        bodyStyle={{
          padding: '16px 16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
        style={{ maxWidth: 500, minWidth: 350, borderColor: color, flex: 1 }}
      >
        <CardHeader color={color}>
          <CardTitle>
            <ColorIndicator color={color} />
            <TreatmentName>{treatmentName}</TreatmentName>
            {description && (
              <Tooltip title={description}>
                <span>
                  <MoreInfoOutlined />
                </span>
              </Tooltip>
            )}
          </CardTitle>

          <ActionButtonGroup>
            {editable && (
              <ActionIconButton
                onClick={() => {
                  scribe.sendEvent(
                    newCreationFlowEvents.clickEditTreatment.name,
                    {
                      experiment_name: form.getFieldValue(ExpFormField.NAME),
                      STATE_NAME:
                        newCreationFlowEvents.clickEditTreatment.state,
                    },
                  );
                  setShowEditTreatmentModal(true);
                }}
              >
                <EditableOutlined />
              </ActionIconButton>
            )}
            {editable && deletable && (
              <Divider
                type="vertical"
                style={{ margin: 0, borderInlineStartColor: '#3D3D3D' }}
              />
            )}
            {deletable && (
              <ActionIconButton
                onClick={() => {
                  scribe.sendEvent(
                    newCreationFlowEvents.clickDeleteTreatment.name,
                    {
                      experiment_name: form.getFieldValue(ExpFormField.NAME),
                      STATE_NAME:
                        newCreationFlowEvents.clickDeleteTreatment.state,
                    },
                  );
                  setShowDeleteTreatmentModal(true);
                }}
              >
                <TrashCanOutlined />
              </ActionIconButton>
            )}
          </ActionButtonGroup>
        </CardHeader>
        {empty ? (
          renderAddVariableEmptyState()
        ) : (
          <>
            <strong style={{ fontSize: 14 }}>Variables</strong>
            <div>
              {variables.map((variable) => (
                <Form.Item
                  key={variable.name}
                  label={
                    <div style={{ fontSize: 12, wordBreak: 'break-word' }}>
                      {variable.name}{' '}
                      <FormItemType>({variable.type})</FormItemType>
                    </div>
                  }
                  required={false} // set false to remove the red star on the left
                  rules={getRules(variable)}
                  name={ExpFormField.VARIABLES.TREATMENT_VALUE(
                    variable.name,
                    treatmentId,
                  )}
                >
                  {renderInput(variable, treatmentId)}
                </Form.Item>
              ))}
            </div>
          </>
        )}
      </Card>
      {showEditTreatmentModal && (
        <EditTreatmentModal
          currentName={treatmentName}
          currentDescription={description || ''}
          onCancel={() => {
            setShowEditTreatmentModal(false);
          }}
          onOk={(name, description) => {
            onEdit(name, description);
            setShowEditTreatmentModal(false);
          }}
        />
      )}
      {showEditJsonModal.show && (
        <JsonEditorModal
          readOnly={disabled}
          data={showEditJsonModal.value}
          jsonSchema={showEditJsonModal.jsonSchema}
          onCancel={() => {
            setShowEditJsonModal({
              show: false,
              variableName: null,
              value: '',
              jsonSchema: '',
            });
          }}
          onOk={(jsString) => {
            form.setFieldValue(
              ExpFormField.VARIABLES.TREATMENT_VALUE(
                showEditJsonModal.variableName,
                treatmentId,
              ),
              jsString,
            );
            setShowEditJsonModal({
              show: false,
              variableName: null,
              value: '',
              jsonSchema: '',
            });
          }}
        />
      )}
      {showDeleteTreatmentModal && (
        <DeleteTreatmentModal
          treatmentName={treatmentName}
          onCancel={() => {
            setShowDeleteTreatmentModal(false);
          }}
          onOk={() => {
            onDelete();
            setShowDeleteTreatmentModal(false);
          }}
        />
      )}
    </>
  );
};

export const CardHeader = styled.div`
  align-items: center;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: ${(props) => `${props.color}1A`};
`;

export const CardTitle = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  line-height: 0px;
`;

export const TreatmentName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

export const ActionButtonGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

export const ActionIconButton = styled.div`
  cursor: pointer;
  line-height: 0px;
`;

export const InputContainer = styled.div`
  align-items: center;
  display: flex;
  height: 40px;

  .error {
    border: 1px solid red;
    border-radius: 12px;
  }
`;

export const BooleanInputContainer = styled.div`
  align-items: center;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  display: flex;
  height: 100%;
  padding: 4px 10px 4px 16px;
  width: 100%;
`;

export const FormItemType = styled.span`
  color: #a3a3a3;
`;

export default TreatmentCard;
